import { useUserStore } from "@/stores/userStore";
import { IndicatorItem, lookupItem, SelectOption } from "./types";
import { PERMISSIONS } from "@/types/constants";
import type { NavigationCategory } from "@/lib/navigation/config";
import { proxyAction } from "./auth/proxyAction";

export function formatString(str: string) {
  if (typeof str !== 'string' || !str) {
    return "";
  }
  const tempString = str.replace(/_/g, " ").toLowerCase();

  return tempString.replace(/\b\w/g, (char) => char.toUpperCase());
}

export function cn(...classes: (string | undefined | false)[]) {
  return classes.filter(Boolean).join(" ");
}

export const getMaxDateTime = () => {
  const now = new Date();
  // Offset for local timezone to ensure accurate "Now"
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
};

export function normalizeRegion(value: any): string {
  if (!value && value !== "") return "";
  if (typeof value === "object") {
    if ("target" in value && value.target && "value" in value.target) {
      return String(value.target.value).trim();
    }
    if ("value" in value) return String(value.value).trim();
    return String(value).trim();
  }
  return String(value).trim();
}

export const hasPermission = (
  requiredPermissions: string[],
  mode: "any" | "all" = "any"
): boolean => {
  const user = useUserStore.getState().user;

  if (!user || !user.permissions) return false;

  const { permissions } = user;

  if (mode === "all") {
    return requiredPermissions.every((perm) => permissions.includes(perm));
  }

  return requiredPermissions.some((perm) => permissions.includes(perm));
};
export const isMobTeam = hasPermission([PERMISSIONS.MOP_TEAM]);

export function cleanState(value: string) {
  return value
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function prettyDateTime(iso: string) {
  const date = new Date(iso);

  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  };

  return date.toLocaleString("en-US", options);
}


export const formatIndicatorNames = (items: IndicatorItem[]) => {
  return items
    .map((item) => item.indicator.name)
    .join(" / ");
};

export function formatETB(
  value: number | string | undefined,
  showCurrency: boolean = true
): string {
  const num = Number(value);

  if (isNaN(num)) return "";

  const formatted = num.toLocaleString("en-ET", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return showCurrency ? `${formatted} ETB` : formatted;
}


export function getFirstAllowedRoute(
  navigation: NavigationCategory[]
): string | null {
  for (const category of navigation) {
    if (category.items.length > 0) {
      return category.items[0].route;
    }
  }
  return null;
}

export function timeAgo(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();

  const diffMs = now.getTime() - date.getTime();
  if (diffMs < 0) return "just now";

  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return `${seconds} sec ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;

  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;

  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks} week${weeks > 1 ? "s" : ""} ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months > 1 ? "s" : ""} ago`;

  const years = Math.floor(days / 365);
  return `${years} year${years > 1 ? "s" : ""} ago`;
}

export const formatShortNumber = (num: number): string => {
  if (num >= 1_000_000_000_000) return (num / 1_000_000_000_000).toLocaleString(undefined, { maximumFractionDigits: 1 }) + "T";
  if (num >= 1_000_000_000) return (num / 1_000_000_000).toLocaleString(undefined, { maximumFractionDigits: 1 }) + "B";
  if (num >= 1_000_000) return (num / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 1 }) + "M";
  if (num >= 1_000) return (num / 1_000).toLocaleString(undefined, { maximumFractionDigits: 1 }) + "K";
  return num.toLocaleString();
};


export function formatHumanDate(
  isoDate: string | Date,
  locale: string = "en-US"
): string {
  const date = typeof isoDate === "string" ? new Date(isoDate) : isoDate;

  if (isNaN(date.getTime())) {
    throw new Error("Invalid date");
  }

  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  }).format(date);
}




export const loadLookup = async (key: string): Promise<SelectOption[]> => {
  const res = await proxyAction<lookupItem[]>(`/base-data/lookups/${key}`, {
    method: "GET",
    auth: true,
  });

  if (!res.success || !Array.isArray(res.data)) {
    return [];
  }

  return res.data.map((r: lookupItem) => ({
    label: r.label,
    value: r.label,
  }));
};
